// src/Component/AuthPage.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Phone, User, Mail, ShieldCheck, Zap, Activity, Plus, Hash, MapPin, Image as ImageIcon, Info, Lock, ArrowRight } from 'lucide-react';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const techShapes = [
    { type: 'square', top: '15%', left: '10%', size: 40, delay: 0, color: 'border-blue-400/40', speed: 20 },
    { type: 'plus', top: '25%', left: '80%', size: 24, delay: 0.5, color: 'text-indigo-400/50', speed: -15 },
    { type: 'circle', top: '70%', left: '15%', size: 60, delay: 1, color: 'border-cyan-400/30', speed: 30 },
    { type: 'dot', top: '80%', left: '85%', size: 12, delay: 1.5, color: 'bg-blue-500/40', speed: -25 },
  ];

  return (
    <section className="relative min-h-screen py-16 md:py-24 bg-[#f8fafc] overflow-hidden font-[Urbanist] flex items-center">
      
      {/* BACKGROUND ELEMENTS */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div animate={{ scale: [1, 1.1, 1], x: [0, 40, 0] }} transition={{ duration: 15, repeat: Infinity }} className="absolute -top-[10%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-blue-300/20 blur-[100px]" />
        <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:32px_32px] opacity-[0.4]"></div>
        {techShapes.map((shape, i) => (
          <motion.div key={i} className="absolute" style={{ top: shape.top, left: shape.left }} animate={{ x: mousePos.x * shape.speed, y: mousePos.y * shape.speed }}>
            {shape.type === 'square' && <div className={`border-2 ${shape.color} rounded-lg`} style={{ width: shape.size, height: shape.size }} />}
            {shape.type === 'plus' && <Plus className={shape.color} size={shape.size} />}
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          
          {/* LEFT COLUMN - Dynamic Content based on isLogin */}
          <div className="w-full lg:w-[45%] h-full flex flex-col justify-center">
            <AnimatePresence mode="wait">
              {isLogin ? (
                <motion.div key="login-text" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="space-y-8 pt-10">
                  <span className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-white/60 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                    <Zap size={14} className="fill-blue-500" /> Welcome Back
                  </span>
                  <h1 className="text-4xl md:text-6xl font-black text-slate-900 leading-tight">
                    Access Your <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">Trading Dashboard</span>
                  </h1>
                  <p className="text-lg text-slate-600 font-medium leading-relaxed">
                    Login to monitor your portfolio, track live algorithms, and manage your trades effortlessly.
                  </p>
                  <div className="flex gap-4 p-5 bg-white/70 rounded-3xl border border-white shadow-xl backdrop-blur-md max-w-xs">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Activity className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">System Status</div>
                      <div className="text-2xl font-black text-slate-900">Online & Active</div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="register-text" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="space-y-8 lg:pt-14 pt-14">
                  <span className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-white/60 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest backdrop-blur-md ">
                    <Zap size={14} className="fill-blue-500" /> Power your trades
                  </span>
                  <h1 className="text-4xl md:text-6xl font-black text-slate-900 leading-tight">
                    Advantage <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-700">Motilal Algo</span> Setup
                  </h1>
                  <p className="text-lg text-slate-600 font-medium leading-relaxed">Lightning-fast algo execution and zero manual errors. Join <strong className="text-blue-700">10,000+ traders</strong> daily.</p>
                  <div className="flex gap-4 p-5 bg-white/70 rounded-3xl border border-white shadow-xl backdrop-blur-md max-w-xs">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"><Activity className="text-blue-600" /></div>
                    <div>
                      <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Accuracy</div>
                      <div className="text-2xl font-black text-slate-900">88%</div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT COLUMN - Flipping Container */}
          <div className="w-full lg:w-[55%]" style={{ perspective: '1200px' }}>
            <AnimatePresence mode="wait">
              {isLogin ? (
                <motion.div 
                  key="login-form" 
                  initial={{ rotateY: 90, opacity: 0 }} 
                  animate={{ rotateY: 0, opacity: 1 }} 
                  exit={{ rotateY: -90, opacity: 0 }} 
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="w-full max-w-md mx-auto origin-center"
                >
                  <LoginForm toggleView={() => setIsLogin(false)} />
                </motion.div>
              ) : (
                <motion.div 
                  key="register-form" 
                  initial={{ rotateY: -90, opacity: 0 }} 
                  animate={{ rotateY: 0, opacity: 1 }} 
                  exit={{ rotateY: 90, opacity: 0 }} 
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className="w-full origin-center"
                >
                  <RegisterForm toggleView={() => setIsLogin(true)} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </section>
  );
};

// ==========================================
// SUB-COMPONENTS
// ==========================================

const LoginForm = ({ toggleView }: { toggleView: () => void }) => {
  const [data, setData] = useState({ loginId: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!data.loginId.trim() || !data.password.trim()) {
      setErrors({
        loginId: !data.loginId.trim() ? "Required" : "",
        password: !data.password.trim() ? "Required" : ""
      });
      return;
    }
    
    try {
      const resp = await axios.post('https://motilalalgo.com/api/user/login', {
        login_id: data.loginId,
        password: data.password
      });

      if (resp.status === 200) {
        // YAHAN FIX KIYA HAI: User ko local storage me save karna zaruri hai warna AccountLayout redirect kar dega
        localStorage.setItem('user', JSON.stringify(resp.data.user));

        Swal.fire({ title: 'Welcome Back!', text: 'Login Successful.', icon: 'success', timer: 1500, showConfirmButton: false });
        setTimeout(() => navigate('/account/dashboard'), 1500);
      }
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        Swal.fire({ title: 'Server Down!', text: 'Cannot connect to backend server. Please make sure node server is running on port 10001.', icon: 'warning' });
      } else {
        Swal.fire({ title: 'Error!', text: error.response?.data?.error || 'Invalid Credentials', icon: 'error' });
      }
    }
  };

  return (
    <div className="bg-white/80 border border-white rounded-[2.5rem] p-8 md:p-10 shadow-2xl backdrop-blur-xl">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-black text-slate-900">Login</h2>
        <p className="text-slate-500 font-medium mt-2">Enter your credentials to continue</p>
      </div>
      <form onSubmit={handleOnSubmit} className="space-y-5">
        <InputField label="Login ID / Email" name="loginId" value={data.loginId} onChange={handleOnChange} error={errors.loginId} icon={<User size={18}/>} placeholder="Enter your Login ID" />
        <div>
          <div className="flex justify-between items-end mb-1.5">
            <label className="block text-[13px] font-bold text-slate-600 tracking-wide">Password</label>
            <Link to="/forgot-password" className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors">Forgot Password?</Link>
          </div>
          <InputField name="password" type="password" value={data.password} onChange={handleOnChange} error={errors.password} icon={<Lock size={18}/>} placeholder="Enter your password" hideLabel={true} />
        </div>
        <div className="pt-4">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="w-full bg-slate-900 hover:bg-blue-600 text-white font-black py-4 rounded-xl shadow-[0_10px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_10px_20px_rgba(37,99,235,0.3)] transition-all duration-300 uppercase tracking-widest text-sm flex items-center justify-center gap-2">
            Secure Login <ArrowRight size={18} />
          </motion.button>
        </div>
      </form>
      <div className="mt-8 text-center border-t border-slate-200 pt-6">
        <p className="text-[13px] font-semibold text-slate-600">
          Don't have an account yet?{' '}
          <button type="button" onClick={toggleView} className="text-blue-600 font-bold hover:underline">Create Demat Account</button>
        </p>
      </div>
    </div>
  );
};

const RegisterForm = ({ toggleView }: { toggleView: () => void }) => {
  const [data, setData] = useState({ firstName: '', lastName: '', email: '', mobile: '', accountNumber: '', ifscCode: '', aadharNumber: '', panNumber: '', city: '', dob: '' });
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    if (name === 'ifscCode') value = value.toUpperCase().slice(0, 11);
    if (name === 'panNumber') value = value.toUpperCase().slice(0, 10);
    if (name === 'mobile') value = value.replace(/\D/g, '').slice(0, 10);
    if (name === 'accountNumber') value = value.replace(/\D/g, '').slice(0, 18);
    if (name === 'aadharNumber') {
      let raw = value.replace(/\D/g, '').slice(0, 12);
      value = raw.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPhoto(e.target.files[0]);
      if (errors.photo) setErrors((prev) => ({ ...prev, photo: '' }));
    }
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!data.firstName || !data.email || !data.mobile || !data.aadharNumber || !photo) {
        Swal.fire({ title: 'Incomplete Details', text: 'Please fill all required fields and upload your passport photo.', icon: 'warning' });
        return;
    }

    const formData = new FormData();
    
    Object.entries(data).forEach(([key, val]) => {
        if (key === 'aadharNumber') {
            formData.append(key, val.replace(/\s/g, '')); 
        } else {
            formData.append(key, val);
        }
    });

    if (photo) {
        formData.append('passportPhoto', photo);
    }

    try {
      const resp = await axios.post('https://motilalalgo.com/api/user/register', formData, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      if (resp.status === 201 || resp.status === 200) {
        Swal.fire({
          title: 'Application Submitted!',
          html: `
            <div style="text-align: center; padding: 10px;">
              <p style="color: #475569; font-size: 15px; margin-bottom: 15px; line-height: 1.5;">
                Thank you for choosing <strong>Motilal Algo</strong>. Your Demat account application has been received and is currently under review.
              </p>
              <div style="background: #eff6ff; padding: 15px; border-radius: 8px; border: 1px dashed #3b82f6;">
                <p style="color: #1e40af; font-weight: 600; font-size: 14px; margin: 0;">
                  Our verification team will review your details and contact you shortly with your secure Login ID and Password.
                </p>
              </div>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Proceed to Login Portal',
          confirmButtonColor: '#0f172a',
          allowOutsideClick: false
        }).then((result) => {
          if (result.isConfirmed) {
             toggleView(); 
          }
        });
      }
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        Swal.fire({ title: 'Server Unreachable!', text: 'Please check if your backend server is running on port 10001.', icon: 'warning' });
      } else {
        Swal.fire({ title: 'Registration Failed!', text: error.response?.data?.error || 'Form Submission Failed', icon: 'error' });
      }
    }
  };

  return (
    <div className="bg-white/80 border border-white rounded-[2.5rem] p-6 md:p-10 shadow-2xl backdrop-blur-xl">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-black text-slate-900">Get <span className="text-blue-600">Free Demat Account</span></h2>
        <p className="text-slate-500 font-medium">Complete your registration in 5 minutes</p>
      </div>
      <form onSubmit={handleOnSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <InputField label="First Name" name="firstName" value={data.firstName} onChange={handleOnChange} error={errors.firstName} icon={<User size={18}/>} placeholder="First name" />
        <InputField label="Last Name" name="lastName" value={data.lastName} onChange={handleOnChange} error={errors.lastName} icon={<User size={18}/>} placeholder="Last name" />
        <InputField label="Email Address" name="email" value={data.email} onChange={handleOnChange} error={errors.email} icon={<Mail size={18}/>} placeholder="example@gmail.com" />
        <InputField label="Date of Birth" name="dob" type="date" value={data.dob} onChange={handleOnChange} error={errors.dob} icon={<Activity size={18}/>} />
        <InputField label="Mobile Number" name="mobile" value={data.mobile} onChange={handleOnChange} error={errors.mobile} icon={<Phone size={18}/>} placeholder="10-digit number" />
        <InputField label="Account Number" name="accountNumber" value={data.accountNumber} onChange={handleOnChange} error={errors.accountNumber} icon={<Hash size={18}/>} placeholder="A/C Number" />
        <InputField label="IFSC Code" name="ifscCode" value={data.ifscCode} onChange={handleOnChange} error={errors.ifscCode} icon={<Zap size={18}/>} placeholder="11 chars" />
        <InputField label="Aadhar Number" name="aadharNumber" value={data.aadharNumber} onChange={handleOnChange} error={errors.aadharNumber} icon={<ShieldCheck size={18}/>} placeholder="XXXX XXXX XXXX" />
        <InputField label="PAN Number" name="panNumber" value={data.panNumber} onChange={handleOnChange} error={errors.panNumber} icon={<ShieldCheck size={18}/>} placeholder="10 chars" />
        <InputField label="City" name="city" value={data.city} onChange={handleOnChange} error={errors.city} icon={<MapPin size={18}/>} placeholder="Enter city" />
        
        <div className="relative group">
          <label className="block text-[13px] font-bold text-slate-600 mb-1.5 tracking-wide">Passport Size Photo</label>
          <div className={`relative flex items-center bg-slate-50/80 border ${errors.photo ? 'border-red-400' : 'border-slate-200'} rounded-xl p-3 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all`}>
            <ImageIcon size={18} className="text-blue-500/70 mr-3" />
            <input type="file" accept="image/*" onChange={handleFileChange} className="w-full text-xs font-semibold file:hidden cursor-pointer" />
            <span className="absolute right-4 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md pointer-events-none">
              {photo ? 'Selected' : 'Choose'}
            </span>
          </div>
          {errors.photo && <p className="text-red-500 text-[10px] font-bold mt-1">{errors.photo}</p>}
        </div>

        <div className="md:col-span-2 bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-3">
          <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
          <div className="text-[11px] leading-relaxed text-slate-600">
            <p className="font-bold text-blue-800 uppercase mb-1">Upload Guidelines:</p>
            Ensure face is fully visible. <span className="font-bold">No caps, masks, or sunglasses allowed.</span> Eyes must be open and looking straight at the camera.
          </div>
        </div>

        <div className="md:col-span-2 pt-4">
          <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" className="w-full bg-slate-900 text-white font-black py-4 rounded-xl shadow-lg uppercase tracking-widest text-sm flex items-center justify-center gap-2">
            Open Account <Zap size={16} className="fill-white" />
          </motion.button>
        </div>

        <div className="md:col-span-2 mt-4 text-center">
          <p className="text-[13px] font-semibold text-slate-600">
            Already registered?{' '}
            <button type="button" onClick={toggleView} className="text-blue-600 font-bold hover:underline">
              Login here
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

// Reusable Input Sub-component
const InputField = ({ label, name, value, onChange, error, icon, placeholder, type = "text", hideLabel = false }: any) => (
  <div className="relative group">
    {!hideLabel && <label htmlFor={name} className="block text-[13px] font-bold text-slate-600 mb-1.5 tracking-wide">{label}</label>}
    <div className="relative">
      <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-transform group-focus-within:scale-110 ${error ? 'text-red-500' : 'text-blue-500/70'}`}>{icon}</div>
      <input type={type} name={name} value={value} onChange={onChange} id={name} placeholder={placeholder}
        className={`w-full pl-11 pr-4 py-3 bg-slate-50/80 border ${error ? 'border-red-400 focus:ring-red-500/10' : 'border-slate-200 focus:ring-blue-500/10 focus:border-blue-500'} rounded-xl text-slate-800 text-sm font-semibold transition-all outline-none focus:ring-4`} 
      />
    </div>
    {error && <p className="text-red-500 text-[10px] font-bold mt-1">{error}</p>}
  </div>
);

export default AuthPage;